import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnDestroy,
  output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { AccordionMenu } from '../../molecules/accordion-menu/accordion-menu.component';
import {
  AccordionExpandPosition,
  AccordionItem,
} from '../../../models/accordion.models';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { IconComponent } from '@ui';

@Component({
  selector: 'ui-administration-sidebar-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AccordionMenu,
    NzCheckboxModule,
    ReactiveFormsModule,
    TranslocoDirective,
    IconComponent
  ],
  template: `
    <ui-accordion-menu
      [panels]="panels()"
      [expandIconPosition]="expandIconPosition()"
      (onSubitemSelect)="emitSubitemSelect($event)"
    />
    <ng-template #headerTemplate let-data>
      <ng-container *transloco="let t">
        <div class="flex flex-row py-2 px-1 justify-start items-center">
          <label class="font-bold">
            <ui-icon class="mr-1" [icon]="data.preIcon"></ui-icon>
            {{ t(data.title) }}
          </label>
        </div>
      </ng-container>
    </ng-template>
  `,
})
export class AdministrationSidebarMenuComponent implements OnDestroy {
  menu = input.required<AccordionItem[]>();
  expandIconPosition = input<AccordionExpandPosition>('end');
  onSubitemSelect = output<{
    itemId: string | null;
    subitemId: string | null;
    itemActive?: boolean;
  }>();
  onFormChange = output<{ id: string; checked: boolean }[]>();

  /**
   * Computes the panels array for the accordion menu, adding header templates and context.
   */
  panels = computed(() => {
    return this.menu().map((panel, index) => ({
      ...panel,
      header: this.headerTemplate()!,
      index,
      context: { ...panel.context, index, id: panel.id },
    }));
  });
  headerTemplate = viewChild<TemplateRef<any>>('headerTemplate');


  ngOnDestroy(): void {
    // Ensure that in the first panel the item with id === 'rsabt' is active
    // and all other items in that panel are inactive. Try to update the
    // input signal if it's writable, otherwise mutate in-place as a best-effort.
    try {
      const currentMenu = this.menu ? this.menu() : null;
      if (currentMenu && currentMenu.length > 0 && Array.isArray(currentMenu[0].items)) {
        const items = currentMenu[0].items as any[];
        // Find if any item has id 'rsabt'
        const hasRsabt = items.some((it) => it && it.id === 'rsabt');
        if (hasRsabt) {
          // Clone to avoid mutating parent's reference when possible
          const cloned = JSON.parse(JSON.stringify(currentMenu));
          const clonedItems = cloned[0].items as any[];
          for (let i = 0; i < clonedItems.length; i++) {
            clonedItems[i].active = clonedItems[i].id === 'rsabt';
          }

          const menuSignal: any = this.menu as any;
          if (menuSignal && typeof menuSignal.set === 'function') {
            menuSignal.set(cloned);
          } else {
            // fallback: mutate in-place
            for (let i = 0; i < items.length; i++) {
              try {
                items[i].active = items[i].id === 'rsabt';
              } catch (e) {
                // ignore per-item failures
              }
            }
          }
        }
      }
    } catch (e) {
      // ignore errors during destroy-time update
    }

    // Emit selection so parent can react as before
    this.onSubitemSelect.emit({ itemId: 'alarmsConfig', subitemId: 'rsabt', itemActive: true });
  }

  /**
   * Emits the subitem selection event to the parent component.
   * @param event - Object containing itemId, subitemId, and optional itemActive.
   */
  emitSubitemSelect(event: {
    itemId: string | null;
    subitemId: string | null;
    itemActive?: boolean;
  }) {
    const { itemId, subitemId, itemActive } = event;
    this.onSubitemSelect.emit({
      itemId,
      subitemId,
      itemActive,
    });
  }

}
